import { ShieldAlert } from 'lucide-react';
import { useMemo, useState } from 'react';

import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';

import { Datatable } from '@@/datatables';
import { BasicTableSettings, createPersistedStore } from '@@/datatables/types';
import { useTableState } from '@@/datatables/useTableState';
import { Alert } from '@@/Alert';

import { useIsVulnerabilityScanningEnabled } from '../queries/useIsVulnerabilityScanningEnabled';
import {
  isForbiddenError,
  useVulnerabilityReportTable,
} from '../queries/useVulnerabilityReportTable';
import { VulnerabilityDetailsModal } from '../VulnerabilityDetailsModal';

import { createColumns } from './columns';
import { fromTable } from './fromTable';
import { VulnerabilityRow } from './types';

const storageKey = 'kubernetes.vulnerabilities';

// No auto-refresh setting: trivy-operator rewrites reports on a 24h TTL, so
// polling would only add noise. The page header's reload covers the rest.
const settingsStore = createPersistedStore<BasicTableSettings>(storageKey, {
  id: 'severity',
  desc: false,
});

export function VulnerabilitiesDatatable() {
  const tableState = useTableState(settingsStore, storageKey);
  const environmentId = useEnvironmentId();

  const [selected, setSelected] = useState<VulnerabilityRow | null>(null);

  const scanningEnabledQuery = useIsVulnerabilityScanningEnabled(environmentId);
  const isScanningEnabled = scanningEnabledQuery.data ?? false;

  const reportsQuery = useVulnerabilityReportTable({
    environmentId,
    enabled: isScanningEnabled,
    select: fromTable,
  });

  const columns = useMemo(() => createColumns(setSelected), []);
  const rows = reportsQuery.data ?? [];

  if (scanningEnabledQuery.isSuccess && !isScanningEnabled) {
    return (
      <Alert color="info" title="Vulnerability scanning is not enabled">
        No vulnerability scanning policy is applied to this environment, so
        nothing is scanning its images. Attach a vulnerability scanning policy
        to one of its environment groups to start collecting findings.
      </Alert>
    );
  }

  // Scanning is known to be on by this point, so the only remaining causes are
  // the caller's access and the cluster itself. Saying so separately beats one
  // message that lists everything it might have been.
  if (reportsQuery.isError) {
    return isForbiddenError(reportsQuery.error) ? (
      <Alert color="warn" title="You do not have access to the scanner reports">
        Reading findings requires access to aquasecurity.github.io resources in
        this cluster, which your role does not grant. An environment
        administrator can view them.
      </Alert>
    ) : (
      <Alert color="error" title="Unable to read vulnerability reports">
        The scanner reports could not be read from this environment. The scanner
        may still be starting up, or the cluster may be unreachable.
      </Alert>
    );
  }

  return (
    <>
      <Datatable<VulnerabilityRow>
        dataset={rows}
        columns={columns}
        settingsManager={tableState}
        isLoading={scanningEnabledQuery.isLoading || reportsQuery.isLoading}
        title="Image vulnerabilities"
        titleIcon={ShieldAlert}
        description="Findings reported by the cluster's image scanner, one row per scanned container."
        getRowId={(row) => row.id}
        disableSelect
        emptyContentLabel="No vulnerability reports found"
        data-cy="k8s-vulnerabilities-datatable"
      />

      {selected && (
        <VulnerabilityDetailsModal
          environmentId={environmentId}
          namespace={selected.namespace}
          resourceName={selected.workload}
          resourceKind={selected.resourceKind}
          onDismiss={() => setSelected(null)}
        />
      )}
    </>
  );
}
